import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {CarePlanServiceGroupType} from "../models/care-plan-service-group-type.model";
import {CarePlanServiceGroupTypeDgSearchResults} from "../models/datagrid-search-results/care-plan-service-group-type.dg-search-results.model";

/**
 * Serwis zarządzający słownikiem typów grup usług dla Abonamentów (Care Plans).
 * Pozwala na strukturyzowanie benefitów abonamentowych w logiczne kategorie.
 * Implementuje standardowy interfejs DataGrid do obsługi list tabelarycznych (CRUD).
 */
@Injectable({
  providedIn: 'root'
})
export class CarePlanServiceGroupTypeService implements DataGridServiceInterface<Observable<CarePlanServiceGroupTypeDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę typów grup usług dla tabeli danych (DataGrid).
   *
   * @param limit Maksymalna liczba rekordów do zwrócenia na jednej stronie.
   * @param offset Przesunięcie wyników (od którego elementu zacząć pobieranie).
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Nazwa kolumny, po której następuje sortowanie.
   * @param showDeletedObjects Flaga określająca, czy dołączyć rekordy miękko usunięte (Archiwum).
   * @returns Observable emitujący ustandaryzowany obiekt wyników zawierający instancje typów grup.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<CarePlanServiceGroupTypeDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/care-plan-services-group-types/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new CarePlanServiceGroupTypeDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new CarePlanServiceGroupType(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);

      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje typy grup usług na podstawie zdefiniowanych filtrów tekstowych,
   * z zachowaniem paginacji i sortowania.
   *
   * @param limit Maksymalna liczba wyników na stronie.
   * @param offset Przesunięcie wyników wyszukiwania.
   * @param searchFieldsData Mapa zawierająca pary klucz-wartość (pole -> szukana fraza).
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga określająca widoczność usuniętych rekordów.
   * @returns Observable emitujący przefiltrowany obiekt wyników DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<CarePlanServiceGroupTypeDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/care-plan-services-group-types/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new CarePlanServiceGroupTypeDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new CarePlanServiceGroupType(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa wybrany typ grupy z systemu.
   *
   * @param uuid Globalnie unikalny identyfikator usuwanej grupy.
   * @returns Observable potwierdzający usunięcie ('ok') lub zwracający undefined w przypadku błędu.
   */
  delete(uuid: any) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/care-plan-services-group-types/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return 'ok';
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Tworzy nowy typ grupy usług w słowniku.
   *
   * @param carePlanServiceGroupType Nowy obiekt grupy do zapisania.
   * @returns Observable emitujący zapisany obiekt wraz z nadanym przez serwer UUID.
   */
  add(carePlanServiceGroupType: CarePlanServiceGroupType) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/care-plan-services-group-types', carePlanServiceGroupType).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<CarePlanServiceGroupType>(new CarePlanServiceGroupType(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje istniejący typ grupy usług.
   *
   * @param carePlanServiceGroupType Obiekt grupy zawierający zmodyfikowane dane.
   * @returns Observable emitujący zaktualizowany obiekt grupy z serwera.
   */
  update(carePlanServiceGroupType: CarePlanServiceGroupType) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/care-plan-services-group-types', carePlanServiceGroupType).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<CarePlanServiceGroupType>(new CarePlanServiceGroupType(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
